import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  BadgeComponent,
  BreadcrumbsComponent,
  ButtonComponent,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  IconComponent,
  LabelComponent,
  ModalService,
  NotificationService,
  OptionComponent,
  PageHeaderComponent,
  PlaceholderComponent,
  SelectComponent,
  Table2Component
} from '@pansa/ui';
import { SortState } from '@app/shared/models/sort.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { FilterSortService } from '@app/shared/services/filter-sort.service';
import { AuthorizedEntityDTO } from '../../models/authorized-entity.model';
import { AuthorizedEntityService } from '../../services/authorized-entity.service';
import { GroupTypeEnum } from '@app/features/groups/models/group.model';
import { GroupColorPipe } from '@app/features/groups/pipes/group-color.pipe';
import { AuthorizedEntityDetailsService } from '../../services/authorized-entity-details.service';
import { AuthorizedEntityDetailsComponent } from '../authorized-entity-details/authorized-entity-details.component';
import { AuthorizedEntityUsersComponent } from '../authorized-entity-users/authorized-entity-users.component';
import { tap } from 'rxjs';
import { httpResource } from '@angular/common/http';

@Component({
  selector: 'rqa-authorized-entity-list',
  templateUrl: './authorized-entity-list.component.html',
  styleUrl: './authorized-entity-list.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    BreadcrumbsComponent,
    ButtonComponent,
    IconComponent,
    Table2Component,
    DividerComponent,
    EmptyStateComponent,
    OptionComponent,
    SelectComponent,
    PlaceholderComponent,
    LabelComponent,
    FormFieldComponent,
    GroupColorPipe,
    BadgeComponent,
    AuthorizedEntityDetailsComponent,
    AuthorizedEntityUsersComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class AuthorizedEntityListComponent {
  private readonly authorizedEntityService = inject(AuthorizedEntityService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly filterService = inject<FilterSortService<AuthorizedEntityDTO>>(FilterSortService);
  private readonly authorizedEntityDetailsService = inject(AuthorizedEntityDetailsService);
  private readonly notificationService = inject(NotificationService)
  

  filtersForm = new FormGroup({
    global: new FormControl(''),
    group: new FormControl('Wszystkie')
  });

  filters = toSignal(this.filtersForm.valueChanges, { initialValue: this.filtersForm.value });

  readonly sort = signal<SortState>({
    key: 'id',
    direction: ''
  });

  filteredAuthorizedEntity = this.filterService.connect({
    data: this.authorizedEntityService.authorizedEntityResource.value,
    filters: this.filters,
    sort: this.sort
  });

  readonly expandedGroups = signal<Set<string>>(new Set());
  readonly groupUsers = signal<Map<string, AuthorizedEntityDTO[]>>(new Map());

  trackBy: keyof AuthorizedEntityDTO = 'uuid';
  expandedRows = new Set<any>();
  groupTypeOptions: string[] = Object.values(GroupTypeEnum);
  selectedRow: AuthorizedEntityDTO | null = null;

  onSortChange(sort: SortState) {
    this.sort.set(sort);
  }

  onEdit(authorizedEntity: AuthorizedEntityDTO, event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(['/authorized-entity/edit', authorizedEntity.uuid]);
  }

  onAdd(): void {
    this.router.navigate(['/authorized-entity/add']);
  }

  onDelete(authorizedEntity: AuthorizedEntityDTO, event: MouseEvent): void {
    event.stopPropagation();
    this.modalService.openModal({
      type: 'warning',
      destructive: true,
      title: `Czy chcesz usunąć podmiot ${authorizedEntity.name}`,
      subTitle: 'Usunięcie podmiotu jest trwałe',
      submitText: 'Usuń podmiot',
      cancelText: 'Anuluj',
      onSubmit: () => this.deleteAuthorizedEntity(authorizedEntity.uuid)
    });
  }

  onShowDetails(authorizedEntity: AuthorizedEntityDTO, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedRow = authorizedEntity;
    this.authorizedEntityDetailsService.open(authorizedEntity);
  }

  onAddUser(authorizedEntity: AuthorizedEntityDTO, event: MouseEvent): void {
    this.router.navigate([`/authorized-entity/${authorizedEntity.uuid}/add-person`]);
    event.stopPropagation();
  }

  onToggleRow(row: AuthorizedEntityDTO, event: MouseEvent): void {
    event.stopPropagation();
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
    } else {
      this.expandedRows.add(row);
    }
  }

  isExpanded(row: any): boolean {
    return this.expandedRows.has(row);
  }

  private deleteAuthorizedEntity(id: string): void {
    this.authorizedEntityService
      .deleteAuthorizedEntity(id)
      .pipe(
        tap(() => {
          this.notificationService.success({
            title: 'Sukces',
            subTitle: 'Usunięto podmiot uprawniony'
          });
          this.authorizedEntityService.refreshAuthorizedEntity();
        })
      )
      .subscribe();
  }
}
